import React from "react";
import { useTranslation } from "react-i18next";

import { Button, Col, Row, Space, Spin, Table, Typography, Card, Divider } from "antd";

import { itemName, API_URL } from "../../shared/utils/Util";
import {
  centsToDollar,
  formatAddress,
  getFetchT,
  shortDisplayTime,
} from "../../shared/utils/TypedUtil";
import { Item, ItemAttributes, Order, OrderCustomer } from "../../types/model";
import { useAntdTheme, useNotification } from "../../shared/hooks";

const { Text, Title } = Typography;

type OrderDetailProps = {
  restaurantId: string;
  order_id: string;
};

// Group items by item attribute.
type ItemGroup = {
  attributes: ItemAttributes;
  count: number;
};

export const OrderDetail = (props: OrderDetailProps) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { notifyError } = useNotification();
  const [order, setOrder] = React.useState<Order | null>(null);

  const getOrder = () => {
    getFetchT<Order>(
      `${API_URL}/v1/r/${props.restaurantId}/orders/${props.order_id}`,
      setOrder,
      (err) => {
        notifyError(t("failed_to_fetch_data"), err);
      }
    );
  };
  React.useEffect(getOrder, [props]);

  if (order === null) {
    return <Spin size="large" />;
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      {order.customer && <CustomerInfo customer={order.customer} />}
      <ItemTable items={order.items} />
      <Card
        style={{
          borderRadius: token.borderRadiusLG,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          {order.pretax_price && (
            <Row justify="space-between">
              <Text>{t("pretax_price")}:</Text>
              <Text strong>${centsToDollar(order.pretax_price)}</Text>
            </Row>
          )}
          {order.handling_fee && (
            <Row justify="space-between">
              <Text>{t("handling_fee")}:</Text>
              <Text strong>${centsToDollar(order.handling_fee)}</Text>
            </Row>
          )}
          {order.tip && (
            <Row justify="space-between">
              <Text>{t("tip")}:</Text>
              <Text strong>${centsToDollar(order.tip)}</Text>
            </Row>
          )}
        </Space>
      </Card>
    </Space>
  );
};

type CustomerInfoProps = {
  customer: OrderCustomer;
};

const CustomerInfo = (props: CustomerInfoProps) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  return (
    <Card
      title={
        <Text strong style={{ fontSize: token.fontSizeLG }}>
          {t("customer_information")}
        </Text>
      }
      style={{
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorBorder}`,
      }}
      styles={{ body: { padding: token.paddingLG } }}
    >
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Row>
          <Text type="secondary">{t("name")}:</Text>
          <Text strong style={{ marginLeft: token.marginSM }}>
            {props.customer.name}
          </Text>
        </Row>
        <Row>
          <Text type="secondary">{t("phone")}:</Text>
          <Text strong style={{ marginLeft: token.marginSM }}>
            {props.customer.phone}
          </Text>
        </Row>
        <Row>
          <Text type="secondary">{t("email")}:</Text>
          <Text strong style={{ marginLeft: token.marginSM }}>
            {props.customer.email}
          </Text>
        </Row>
        {props.customer.address && (
          <Row>
            <Text type="secondary">{t("address")}:</Text>
            <Text strong style={{ marginLeft: token.marginSM }}>
              {formatAddress(props.customer.address)}
            </Text>
          </Row>
        )}
      </Space>
    </Card>
  );
};

type ItemTableProps = {
  items: Item[];
};

const ItemTable = (props: ItemTableProps) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const item_groups = new Map<string, ItemGroup>();
  props.items.forEach((i: Item) => {
    if (i.attributes) {
      const k = i.attributes.id;
      if (item_groups.has(k)) {
        // @ts-ignore
        item_groups.get(k).count += 1;
      } else {
        item_groups.set(k, { attributes: i.attributes, count: 1 });
      }
    }
  });

  let total_count = 0;
  const item_group_list: ItemGroup[] = [];
  for (const [k, v] of item_groups) {
    total_count += v.count;
    item_group_list.push(v);
  }

  const columns: any = [
    {
      title: t("quantity"),
      dataIndex: "count",
      key: "count",
      width: 80,
    },
    {
      title: t("name"),
      key: "name",
      render: (text: string, record: ItemGroup) => {
        return <Text strong>{itemName(record.attributes)}</Text>;
      },
    },
    {
      title: t("notes"),
      key: "notes",
      render: (text: string, record: ItemGroup) => {
        return (
          <Space direction="vertical" size="small">
            {!!record.attributes.notes && <Text>{record.attributes.notes}</Text>}
            {record.attributes.modifications.map((mod: string, i: number) => {
              return (
                <Text key={i} type="secondary">
                  {mod}
                </Text>
              );
            })}
          </Space>
        );
      },
    },
  ];

  return (
    <Card
      title={
        <Text strong style={{ fontSize: token.fontSizeLG }}>
          {t("items")}
        </Text>
      }
      style={{
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorBorder}`,
      }}
      styles={{ body: { padding: token.paddingLG } }}
    >
      <Table
        columns={columns}
        dataSource={item_group_list}
        pagination={false}
        rowKey={(r: ItemGroup) => r.attributes.id}
        style={{ borderRadius: token.borderRadiusLG }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3}>
                <Row justify="end">
                  <Text strong style={{ fontSize: token.fontSizeLG }}>
                    {t("total_quantity")}: {total_count}
                  </Text>
                </Row>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </Card>
  );
};
